import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonGrid,
  IonItem,
  IonLabel,
  IonList,
  IonRow,
  IonToggle,
  IonToolbar,
} from '@ionic/react'
import React from 'react'
import SingleSelect from '../../../../components/singleSelect/SingleSelect.component'
import { useDispatch, useSelector } from 'react-redux'
import { getAllPNNVOptionsSelector, getVitalOptionsSelector } from '../../../options/selectors'
import { IRootState } from '../../../../store'
import {
  saveVitals,
  setVitalsStrokeScaleValues,
  setStrokeScaleScoreTotal,
  setStrokeScaleScoreTotalPNNV,
  setStrokeScaleScoreTotalPNNVEnabled,
} from '../../store/vitals.actions'
import { useHistory } from 'react-router'
import { useTranslation } from 'react-i18next'
import './StrokeScore.content.css'
import { IVitalOptionsState } from '../../../options/store/options.types'
import { IPNNVOption } from '../../../../common/common.types'

const VitalsStrokeScoreContent: React.FC = () => {
  const vitalOptionsState: IVitalOptionsState = useSelector(getVitalOptionsSelector)
  const { customStrokeScaleScoreOptions } = vitalOptionsState

  const vitalsState = useSelector((state: IRootState) => state.vitals)
  const {
    vitalsStrokeScaleValues,
    strokeScaleScoreTotal,
    strokeScaleScoreTotalPNNV,
    __isStrokeScaleScoreTotalPNNVEnabled,
  } = vitalsState

  const allPNNVOptions = useSelector(getAllPNNVOptionsSelector)
  const strokeScaleScoreNegativeOptions = allPNNVOptions?.['eVitals.29'] || []

  const dispatch = useDispatch()
  const history = useHistory()
  const { t } = useTranslation()

  const displayStrokeScoreTotal = strokeScaleScoreTotalPNNV ? null : strokeScaleScoreTotal || 'N/A'

  const handleStrokeScaleOnChange = (quesId: string, selectedOption: any) => {
    const updatedSelectedOption = { ...selectedOption, quesId }
    const strokeData: any = [...vitalsStrokeScaleValues]
    let score = strokeScaleScoreTotal || 0
    const findIndexEle = strokeData.findIndex((strokeDataObj: any) => strokeDataObj.quesId === quesId)
    if (findIndexEle >= 0 && selectedOption) {
      score = score - strokeData[findIndexEle].fieldNumeric + selectedOption.fieldNumeric
      strokeData[findIndexEle] = updatedSelectedOption
    } else if (findIndexEle >= 0 && !selectedOption) {
      score = score - strokeData[findIndexEle].fieldNumeric
      strokeData.splice(findIndexEle, 1)
    } else {
      strokeData.push(updatedSelectedOption)
      score = score + selectedOption.fieldNumeric
    }
    dispatch(setVitalsStrokeScaleValues(strokeData))
    dispatch(setStrokeScaleScoreTotal(score))
    dispatch(saveVitals())
  }

  const strokeScaleScoreTotalPNNVOnChange = (option: IPNNVOption | null) => {
    dispatch(setStrokeScaleScoreTotalPNNV(option))
    // When PNNV is selected, clear the score and individual question selections
    if (option) {
      dispatch(setStrokeScaleScoreTotal(null))
      dispatch(setVitalsStrokeScaleValues([])) // Reset dynamic picklist data
    }
    dispatch(saveVitals())
  }

  const setTotalReasonEnabledonChange = () => {
    dispatch(setStrokeScaleScoreTotalPNNVEnabled(!__isStrokeScaleScoreTotalPNNVEnabled))
    if (!__isStrokeScaleScoreTotalPNNVEnabled) {
      // Turning toggle ON - clear the score AND all individual question selections
      dispatch(setStrokeScaleScoreTotal(null))
      dispatch(setVitalsStrokeScaleValues([])) // Reset dynamic picklist data
    } else {
      // Turning toggle OFF - clear the PNNV to allow recalculation
      dispatch(setStrokeScaleScoreTotalPNNV(null))
    }
    dispatch(saveVitals())
  }

  const resetStrokeScoreData = () => {
    dispatch(setVitalsStrokeScaleValues([]))
    dispatch(setStrokeScaleScoreTotal(null))
    dispatch(setStrokeScaleScoreTotalPNNV(null))
    dispatch(setStrokeScaleScoreTotalPNNVEnabled(false))
    dispatch(saveVitals())
  }

  return (
    <>
      <IonContent className='ion-margin-bottom' fullscreen>
        <IonList>
          <IonGrid className='ion-no-padding'>
            <IonRow className='mt-4 mb-4'>
              <IonRow className='w-100 flex ion-justify-content-between'>
                <div className='m-8-10'>
                  <IonLabel>Total Score </IonLabel>
                  {displayStrokeScoreTotal && (
                    <IonLabel className='custom-number-label'> {displayStrokeScoreTotal}</IonLabel>
                  )}
                </div>
                <IonItem lines='none'>
                  <IonToggle
                    onClick={setTotalReasonEnabledonChange}
                    color='zoll-blue-secondary'
                    checked={__isStrokeScaleScoreTotalPNNVEnabled}
                  />
                  <IonLabel className='m-5' color='medium'>
                    {t('vitals.strokeScore.NotDone')}
                  </IonLabel>
                </IonItem>
              </IonRow>

              {__isStrokeScaleScoreTotalPNNVEnabled && (
                <>
                  <SingleSelect
                    dataTestID='TotalReason'
                    options={strokeScaleScoreNegativeOptions}
                    listHeader={t('vitals.strokeScore.ReasonNotDone')}
                    label={t('vitals.strokeScore.ReasonNotDone')}
                    pnnvAction={strokeScaleScoreTotalPNNVOnChange}
                    action={() => {
                      /**/
                    }}
                    selectedPNNVOption={strokeScaleScoreTotalPNNV}
                    showPNNVAsNormals={true}
                  />
                  <hr className='field-divider' />
                </>
              )}
            </IonRow>

            {customStrokeScaleScoreOptions.length > 0 && (
              <>
                {customStrokeScaleScoreOptions.map((strokeScaleOptions: any) => {
                  const { question, quesId, answerOptions } = strokeScaleOptions
                  const selectedOption: any = vitalsStrokeScaleValues.length
                    ? vitalsStrokeScaleValues.find((strokeDataObj: any) => strokeDataObj.quesId === quesId)
                    : null

                  return (
                    <React.Fragment key={quesId}>
                      <SingleSelect
                        disabled={__isStrokeScaleScoreTotalPNNVEnabled}
                        dataTestID={question}
                        options={answerOptions}
                        selectedOption={selectedOption}
                        listHeader={question}
                        label={question}
                        action={(e) => {
                          handleStrokeScaleOnChange(quesId, e)
                        }}
                        showSelectedOptionId={'fieldNumeric'}
                      />
                      <hr className='field-divider' />
                    </React.Fragment>
                  )
                })}
              </>
            )}
          </IonGrid>
        </IonList>
      </IonContent>
      <IonFooter>
        <IonToolbar color='light' className='p-10'>
          <IonButtons className='btn-grps'>
            <IonButton
              className='btn btn-tablet'
              color='danger'
              fill='outline'
              expand='block'
              onClick={resetStrokeScoreData}>
              {t('common.buttons.Clear')}
            </IonButton>
            <IonButton
              className='btn btn-tablet btn-primary'
              expand='block'
              onClick={() => {
                dispatch(saveVitals())
                setTimeout(() => {
                  history.back()
                }, 500)
              }}>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </>
  )
}

export default VitalsStrokeScoreContent
